import React from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Labelbox } from "./index";
import EfilingConsentField from "./EfilingConsentField";
import {
  BUSINESS_EFILING_TYPE_OPTIONS,
  BUSINESS_EFILING_PAYMENT_METHOD_OPTIONS,
  BUSINESS_EFILING_INVOICE_STATUS_OPTIONS,
} from "../utils/businessEfilingFormUtils";

const FILING_STATUS_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Efiled", value: "Efiled" },
  { id: "Pending", value: "Pending" },
  { id: "Rejected", value: "Rejected" },
];

function ReadonlyField({ label, value, error, errmsg, required }) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label={label}
      value={value || ""}
      required={required}
      error={error}
      helperText={error ? errmsg : ""}
      InputProps={{ readOnly: true }}
      margin="dense"
      InputLabelProps={{ shrink: true }}
    />
  );
}

/**
 * Shared create/edit fields for Business e-filing (Excel sheet columns).
 */
export default function BusinessEfilingFormFields({
  form,
  setField,
  formErrors = {},
  saving = false,
  invoiceDropdown = [],
  onInvoiceSelect,
  clientId,
  documentCategory = "business",
  hideStatusAndFilerComment = false,
  canEditFilerComment = true,
}) {
  return (
    <Grid container spacing={2} className="efiling-form-grid">
      {/* filing_date is auto-set and saved, but not shown in the form */}
      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="Invoice Number"
          dropdown={invoiceDropdown}
          value={form.invoice_no === "" ? "" : String(form.invoice_no)}
          changeData={(v) => {
            setField("invoice_no", v);
            if (onInvoiceSelect) onInvoiceSelect(v);
          }}
          required
          error={Boolean(formErrors.invoice_no)}
          errmsg={formErrors.invoice_no}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="Invoice Amount"
          value={form.invoice_amount}
          changeData={(v) => setField("invoice_amount", v)}
          required
          error={Boolean(formErrors.invoice_amount)}
          errmsg={formErrors.invoice_amount}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="Invoice Status"
          dropdown={BUSINESS_EFILING_INVOICE_STATUS_OPTIONS}
          value={form.invoice_status || ""}
          changeData={(v) => setField("invoice_status", v)}
          required
          error={Boolean(formErrors.invoice_status)}
          errmsg={formErrors.invoice_status}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="Entity Name"
          value={form.entity_name}
          changeData={(v) => setField("entity_name", v)}
          required
          error={Boolean(formErrors.entity_name)}
          errmsg={formErrors.entity_name}
          disabled={saving}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="State filing"
          value={form.state_filing}
          changeData={(v) => setField("state_filing", v)}
          required
          error={Boolean(formErrors.state_filing)}
          errmsg={formErrors.state_filing}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="Type"
          dropdown={BUSINESS_EFILING_TYPE_OPTIONS}
          value={form.business_type || ""}
          changeData={(v) => setField("business_type", v)}
          required
          error={Boolean(formErrors.business_type)}
          errmsg={formErrors.business_type}
          disabled={saving}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="Fed Payment Method"
          dropdown={BUSINESS_EFILING_PAYMENT_METHOD_OPTIONS}
          value={form.fed_payment_method || ""}
          changeData={(v) => setField("fed_payment_method", v)}
          required
          error={Boolean(formErrors.fed_payment_method)}
          errmsg={formErrors.fed_payment_method}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="Federal Due"
          value={form.federal_due}
          changeData={(v) => setField("federal_due", v)}
          required
          error={Boolean(formErrors.federal_due)}
          errmsg={formErrors.federal_due}
          disabled={saving}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="State Payment Method"
          dropdown={BUSINESS_EFILING_PAYMENT_METHOD_OPTIONS}
          value={form.state_payment_method || ""}
          changeData={(v) => setField("state_payment_method", v)}
          required
          error={Boolean(formErrors.state_payment_method)}
          errmsg={formErrors.state_payment_method}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="State Due"
          value={form.state_due}
          changeData={(v) => setField("state_due", v)}
          required
          error={Boolean(formErrors.state_due)}
          errmsg={formErrors.state_due}
          disabled={saving}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Labelbox
          type="select"
          labelname="CA Payment Method"
          dropdown={BUSINESS_EFILING_PAYMENT_METHOD_OPTIONS}
          value={form.ca_payment_method || ""}
          changeData={(v) => setField("ca_payment_method", v)}
          required
          error={Boolean(formErrors.ca_payment_method)}
          errmsg={formErrors.ca_payment_method}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="CA estimate payment"
          value={form.ca_estimate_payment}
          changeData={(v) => setField("ca_estimate_payment", v)}
          required
          error={Boolean(formErrors.ca_estimate_payment)}
          errmsg={formErrors.ca_estimate_payment}
          disabled={saving}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="Password"
          value={form.password}
          changeData={(v) => setField("password", v)}
          error={Boolean(formErrors.password)}
          errmsg={formErrors.password}
          disabled={saving}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Labelbox
          type="text"
          labelname="Preparer Comments"
          value={form.filer_comment}
          changeData={(v) => setField("filer_comment", v)}
          required
          error={Boolean(formErrors.filer_comment)}
          errmsg={formErrors.filer_comment}
          disabled={saving}
        />
      </Grid>

      {!hideStatusAndFilerComment && canEditFilerComment ? (
        <Grid item xs={12} md={6}>
          <Labelbox
            type="text"
            labelname="Filer Comments"
            value={form.other_comments}
            changeData={(v) => setField("other_comments", v)}
            error={Boolean(formErrors.other_comments)}
            errmsg={formErrors.other_comments}
            disabled={saving}
          />
        </Grid>
      ) : !hideStatusAndFilerComment ? (
        <Grid item xs={12} md={6}>
          <ReadonlyField label="Filer Comments" value={form.other_comments} />
        </Grid>
      ) : null}

      {!hideStatusAndFilerComment ? (
        <Grid item xs={12} md={6}>
          <Labelbox
            type="select"
            labelname="Filing Status"
            dropdown={FILING_STATUS_OPTIONS}
            value={form.filing_status || "Pending"}
            changeData={(v) => setField("filing_status", v)}
            disabled={saving}
            error={Boolean(formErrors.filing_status)}
            errmsg={formErrors.filing_status}
          />
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <EfilingConsentField
          clientId={clientId}
          value={form.signed_consents_link}
          onChange={(v) => setField("signed_consents_link", v)}
          disabled={saving}
          documentCategory={documentCategory}
        />
      </Grid>
    </Grid>
  );
}
